import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Copy, Key, Shield } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { useSecureAuth } from '../../hooks/useSecureAuth';

const AdminCodeGenerator: React.FC = () => {
  useUiLanguage();
  const [description, setDescription] = useState('');
  const [maxUses, setMaxUses] = useState('5');
  const [validDays, setValidDays] = useState('30');
  const [generatedCode, setGeneratedCode] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const { generateSecureAdminCode } = useSecureAuth();

  const handleGenerateCode = async () => {
    if (!description.trim()) {
      toast({
        title: uiText("Description Required"),
        description: uiText("Please provide a description for this admin code."),
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      const code = await generateSecureAdminCode(
        description.trim(),
        parseInt(maxUses) || 5,
        parseInt(validDays) || 30
      );

      if (code) {
        setGeneratedCode(code);
        toast({
          title: uiText("Admin Code Generated"),
          description: uiText("Secure admin code has been generated successfully."),
        });
      }
    } catch (error) {
      console.error('Error generating code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      toast({
        title: uiText("Copied!"),
        description: uiText("Admin code copied to clipboard."),
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: uiText("Copy Failed"),
        description: uiText("Failed to copy to clipboard. Please copy manually."),
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setDescription('');
    setMaxUses('5');
    setValidDays('30');
    setGeneratedCode('');
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          {uiText("Secure Admin Code Generator")}</CardTitle>
        <CardDescription>
          {uiText("Generate secure, time-limited admin codes for new administrator registration.")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!generatedCode ? (
          <>
            <div>
              <Label htmlFor="description">{uiText("Description *")}</Label>
              <Input
                id="description"
                placeholder={uiText("e.g., Code for John Doe - Head Instructor")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxUses">{uiText("Maximum Uses")}</Label>
                <Input
                  id="maxUses"
                  type="number"
                  min="1"
                  max="10"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="validDays">{uiText("Valid for (Days)")}</Label>
                <Input
                  id="validDays"
                  type="number"
                  min="1"
                  max="90"
                  value={validDays}
                  onChange={(e) => setValidDays(e.target.value)}
                />
              </div>
            </div>

            <Button
              onClick={handleGenerateCode}
              disabled={isGenerating || !description.trim()}
              className="w-full bg-red-900 hover:bg-red-800"
            >
              <Shield className="h-4 w-4 mr-2" />
              {isGenerating ? uiText("Generating...") : uiText("Generate Secure Admin Code")}
            </Button>
          </>
        ) : (
          <>
            <Alert className="border-green-200 bg-green-50">
              <Shield className="h-4 w-4" />
              <AlertDescription className="text-green-800">
                <strong>{uiText("Admin Code Generated Successfully!")}</strong>
                <br />
                {uiText("This code can be used")}{' '}{maxUses} {' '}{uiText("time(s) and is valid for")}{' '}{validDays} {' '}{uiText("days.")}</AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>{uiText("Generated Admin Code")}</Label>
              <div className="flex gap-2">
                <Textarea
                  value={generatedCode}
                  readOnly
                  className="bg-gray-100 font-mono text-sm"
                  rows={2}
                />
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Alert className="border-amber-200 bg-amber-50">
              <AlertDescription className="text-amber-800">
                <strong>{uiText("Important:")}</strong> {' '}{uiText("This code will only be shown once. Make sure to copy and securely share it with the intended administrator.")}</AlertDescription>
            </Alert>

            <Button
              onClick={resetForm}
              variant="outline"
              className="w-full"
            >
              {uiText("Generate Another Code")}</Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCodeGenerator;
